import i18next from 'i18next';
import LngDetector from 'i18next-browser-languagedetector';
import { Iterable } from 'immutable';
import moment from 'moment';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { areaUnits, housingType } from '../Property/PropertyModel';
import { DepositLevel } from '../Deposit/Models';

import i18nextWebpackBackend from './i18nextWebpackBackend';
import iggloConfig from '../igglo_config';
import { retry } from '../services/sharedUtils';
import trace from '../services/trace';

// Get countryList file from https://github.com/umpirsky/country-list
const extraLocaleData = {
  'en-us': {
    countryList: () => import(/* webpackChunkName: "country-list-en-us" */ './country-lists/en-us.json')
  },
  fi: {
    moment: () => import('moment/locale/fi'),
    countryList: () => import(/* webpackChunkName: "country-list-fi" */ './country-lists/fi.json'),
    moment_locale_id: 'fi'
  }
};

let countryList = {};

const pathParts = location.pathname.split('/');
export const site = pathParts[1];
export const urlLang = pathParts[2];

const i18nextReady = new BehaviorSubject(false);
const momentReady = new BehaviorSubject(false);
const countryListReady = new BehaviorSubject(false);

export const translationsReady = combineLatest(
  i18nextReady,
  momentReady,
  countryListReady,
  (...values) => values.every(it => it)
).pipe(distinctUntilChanged());

const defaultFallbacks = [];

if (process.env.NODE_ENV !== 'production' || __ENVIRONMENT__ === 'staging') {
  defaultFallbacks.push('dev');
}

const options = {
  whitelist: ['en-us', 'fi', 'dev'],
  fallbackLng: {
    default: defaultFallbacks // Trust production to have good strings.
  },
  lowerCaseLng: true,
  detection: {
    order: ['urlLangDetector']
  },
  interpolation: { escapeValue: false },
  debug: iggloConfig.debug
};


// Better fail at some formatting, than to prevent the loading of the whole app I guess.
// Moment and others are called with true always.
async function getMomentExtra(locale) {
  const extra = extraLocaleData[locale];

  if (extra.moment) {
    try {
      await retry(extra.moment); // Automatic monkey patching happens
    } catch (e) {
      trace.error(e, null, `Could not load moment locale data for ${locale}`);
    }

    moment.locale(extra.moment_locale_id);
  }

  momentReady.next(true);
}

async function getCountryListExtra(locale) {
  const extra = extraLocaleData[locale];

  try {
    countryList = Object.freeze((await retry(extra.countryList)).default);
  } catch (e) {
    trace.error(e, null, `Could not load ISO 3166 locale data for ${locale}`);
  }

  countryListReady.next(true);
}

i18next.on('languageChanged', locale =>
  Promise.all([
    getMomentExtra(locale),
    getCountryListExtra(locale)
  ]).catch(error =>
    trace.error(`An error occurred while loading extra locale data for locale ${locale}`, error)
  )
);

i18next.on('failedLoading', (lng, ns, msg) => {
  trace.error(msg, null, `Failed to load translations for locale '${lng}' for namespace ${ns}`);
});

const lngDetector = new LngDetector();
lngDetector.addDetector({
  name: 'urlLangDetector',
  lookup: () => {
    const parts = urlLang.split('-');
    if (parts.length > 1) {
      // Fallback to en-us if the user tries to access a non existing language,
      // instead of just preventing the whole page from loading up at all.
      // Optimally this should be always handled by the server and not by us.
      return [urlLang, parts[0], 'en-us'];
    }

    return [urlLang, 'en-us'];
  }
});

/* Make sure to attach all event listeners before init.
 * Because we inject the en-us translation manually into I18Next, the init
 * function is run synchronously, thus requiring all event listeners to
 * be attached before it gets called.
 */
i18next
  .use(lngDetector)
  .use(i18nextWebpackBackend)
  .init(options, err => {
    if (err) {
      trace.error(err, 'i18next init error');
    }

    i18nextReady.next(true);
  });

const t = i18next::i18next.t;

function trWithImmutableWarnings(key, parameters) {
  // ImmutableJS 4 will have Immutable.isImmutable for this purpose.
  // All Immutable 3.x models inherit from Iterable, so this should work fine.
  if (Iterable.isIterable(parameters)) {
    // eslint-disable-next-line no-console
    console.warn(`ImmutableJS object used as translation params for key: ${key}`);
  }
  return t(key, parameters);
}

export default (process.env.NODE_ENV === 'development') ? trWithImmutableWarnings : t;

export function mapIsoCodeToCountryName(code) {
  const upper = code.toUpperCase();
  return countryList[upper] || t('common.unknown_country_code', { code: upper });
}

export function getCountryList() { return countryList }

export function formatDate(date) {
  return date.format(t('common.date_format'));
}

export function formatDateTime(dateTime, localTime = true) {
  if (localTime) {
    return dateTime.clone().local().format(t('common.date_time_format'));
  }
  return dateTime.format(t('common.date_time_format'));
}

export function translateHousingType(type) {
  switch (type) {
    case housingType.FLAT: return t('property.types.FLAT');
    case housingType.DETACHED_HOUSE: return t('property.types.DETACHED_HOUSE');
    case housingType.SEMI_DETACHED_HOUSE: return t('property.types.SEMI_DETACHED_HOUSE');
    case 'ANY': return t('common.features.no_preference');
    default: return type;
  }
}

export function translateDepositLevel(level) {
  switch (level) {
    case DepositLevel.ONE_MONTH: return t('deposit.level.one_month');
    case DepositLevel.TWO_MONTHS: return t('deposit.level.two_months');
    case DepositLevel.THREE_MONTHS: return t('deposit.level.three_months');
    default: return '';
  }
}

const sizeTrs = [
  () => t('property.SIZES.room'),
  () => t('property.SIZES.studio'),
  () => t('property.SIZES.one_bedroom'),
  () => t('property.SIZES.two_bedrooms'),
  () => t('property.SIZES.three_bedrooms'),
  () => t('property.SIZES.four_bedrooms'),
  () => t('property.SIZES.lots_of_bedrooms')
];

export function translateApartmentType(type) {
  return sizeTrs[type]?.() ?? type;
}

function m2ToM2(area) { return area }
function m2ToFt2(area) { return Math.round(area * 0.09290304) }
function m2ToYd2(area) { return Math.round(area * 0.83612736) }

function getUnitTransformer(unit) {
  switch (unit) {
    case areaUnits.METER: return m2ToM2;
    case areaUnits.FEET: return m2ToFt2;
    case areaUnits.YARD: return m2ToYd2;
    default: return () => NaN;
  }
}

function trM2() { return t('property.AREA_SHORT.METER') }
function trFt2() { return t('property.AREA_SHORT.FEET') }
function trYd2() { return t('property.AREA_SHORT.YARD') }

function getUnitTr(unit) {
  switch (unit) {
    case areaUnits.METER: return trM2;
    case areaUnits.FEET: return trFt2;
    case areaUnits.YARD: return trYd2();
    default: return () => '';
  }
}

// Memoized version exported
function getAreaFormatterForReal(unit = areaUnits.METER, numberFormatOptions) {
  const fmt = new Intl.NumberFormat({
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    ...numberFormatOptions
  });

  const unitTransformer = getUnitTransformer(unit);
  const unitTr = getUnitTr(unit);

  return area => t('common.area_ftm', {
    area: fmt.format(unitTransformer(area)),
    unit: unitTr()
  });
}

const defaultFormatters = {};
export function getAreaFormatter(unit = areaUnits.METER, numberFormatOptions = {}) {
  if (Object.keys(numberFormatOptions).length === 0) {
    // eslint-disable-next-line no-return-assign
    return defaultFormatters[unit] ||
      (defaultFormatters[unit] = getAreaFormatterForReal(unit, {}));
  }

  return getAreaFormatterForReal(unit, numberFormatOptions);
}

export function summarizePropertyTypeAndSize(property) {
  return t('property.type_size_list', {
    housing_type: translateHousingType(property.housing_type),
    apartment_type: translateApartmentType(property.apartment_type),
    area: getAreaFormatter(property.area_unit)(property.area)
  });
}
